import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Card, Modal, Form, Input, Select, Tag, Popconfirm, message, Tooltip, Badge, Progress } from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined, BarChartOutlined, 
  LineChartOutlined, TeamOutlined, CalendarOutlined, TrophyOutlined, UserOutlined 
} from '@ant-design/icons';
import {
  fetchInstitutions, createInstitution, updateInstitution, deleteInstitution,
  fetchInstitutionCandidateComparison, fetchInstitutionsComparisonOverview
} from '../../../../Service/Institutions_API/institutionsApi';
import InstitutionAcademicYearComparisonModal from './InstitutionAcademicYearComparisonModal';
import CrossInstitutionComparisonModal from './CrossInstitutionComparisonModal';

const InstitutionManagementPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [selectedMedium, setSelectedMedium] = useState(undefined);

  // Single Institution Academic Year Comparison Modal State
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [selectedInstitution, setSelectedInstitution] = useState(null);

  // Cross-Institution Multi-Year Overview Modal State
  const [isOverviewModalOpen, setIsOverviewModalOpen] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewData, setOverviewData] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText && searchText.trim()) params.search = searchText.trim();
      if (selectedMedium && selectedMedium !== 'all') params.medium = selectedMedium;
      const result = await fetchInstitutions(params);
      setData(result);
    } catch (e) {
      message.error('Failed to load institutions.');
    } finally {
      setLoading(false);
    }
  }, [searchText, selectedMedium]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Open single institution academic year comparison
  const [comparisonSelectedMedium, setComparisonSelectedMedium] = useState('all');

  const handleOpenComparison = async (record, medium = 'all') => {
    setSelectedInstitution(record);
    setComparisonSelectedMedium(medium);
    setIsComparisonModalOpen(true);
    setComparisonLoading(true);
    try {
      const res = await fetchInstitutionCandidateComparison(record.institution_id, medium);
      setComparisonData(res);
    } catch (err) {
      console.error('Failed to load candidate comparison:', err);
      message.error('Failed to load academic year candidate comparison.');
    } finally {
      setComparisonLoading(false);
    }
  };

  const handleComparisonMediumChange = async (medium) => {
    if (!selectedInstitution) return;
    setComparisonSelectedMedium(medium);
    setComparisonLoading(true);
    try {
      const res = await fetchInstitutionCandidateComparison(selectedInstitution.institution_id, medium);
      setComparisonData(res);
    } catch (err) {
      console.error('Failed to load candidate comparison for medium:', err);
      message.error('Failed to load candidate comparison for medium.');
    } finally {
      setComparisonLoading(false);
    }
  };

  const handleRefreshComparison = () => {
    if (selectedInstitution) {
      handleOpenComparison(selectedInstitution, comparisonSelectedMedium);
    }
  };

  // Open cross-institution overview
  const handleOpenOverview = async (medium = 'all') => {
    setIsOverviewModalOpen(true);
    setOverviewLoading(true);
    try {
      const res = await fetchInstitutionsComparisonOverview(medium);
      setOverviewData(res);
    } catch (err) {
      console.error('Failed to load overview:', err);
      message.error('Failed to load multi-year comparison overview.');
    } finally {
      setOverviewLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      if (editingItem) {
        await updateInstitution(editingItem.institution_id, values);
        message.success('Institution updated successfully.');
      } else {
        await createInstitution(values);
        message.success('Institution created successfully.');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingItem(null);
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to save institution.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteInstitution(id);
      message.success('Institution deleted successfully.');
      loadData();
    } catch (e) {
      message.error(e.message || 'Failed to delete institution.');
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedMedium(undefined);
  };

  const filteredData = data.filter((item) => {
    const nameMatch = item.institution_name ? item.institution_name.toLowerCase().includes(searchText.toLowerCase()) : false;
    const addressMatch = item.institution_address ? item.institution_address.toLowerCase().includes(searchText.toLowerCase()) : false;
    const matchesSearch = !searchText || nameMatch || addressMatch;

    const matchesMedium = !selectedMedium || item.institution_medium === selectedMedium;

    return matchesSearch && matchesMedium;
  });

  const getMediumTagColor = (medium) => {
    switch (medium) {
      case 'KANNADA': return 'orange';
      case 'ENGLISH': return 'blue';
      case 'KANNADA/ENGLISH': return 'purple';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'institution_id',
      key: 'institution_id',
      width: 70,
      render: id => <strong>#{id}</strong>
    },
    { 
      title: 'Institution Name', 
      key: 'institution_name_medium',
      width: 220,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <strong style={{ color: 'var(--text-color, #000)', fontSize: 13 }}>{record.institution_name}</strong>
          {record.institution_medium && (
            <div>
              <Tag color={getMediumTagColor(record.institution_medium)} style={{ fontSize: 10 }}>
                {record.institution_medium.toUpperCase()}
              </Tag>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Candidate Strength & Years',
      key: 'candidate_stats',
      width: 220,
      sorter: (a, b) => (Number(a.total_candidates) || 0) - (Number(b.total_candidates) || 0),
      render: (_, record) => {
        const totalCand = Number(record.total_candidates || 0);
        const totalYears = Number(record.total_academic_years || 0);
        const avgScore = Number(record.overall_avg_percentage || 0);

        if (totalCand === 0) {
          return <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>No Candidates</span>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Tag color="geekblue" style={{ margin: 0, fontWeight: 700, fontSize: 11 }}>
                <TeamOutlined style={{ marginRight: 4 }} />
                {totalCand.toLocaleString()} Students
              </Tag>
              <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                {totalYears} AYs
              </Tag>
            </div>
            {avgScore > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                <span>Avg Score: </span>
                <strong style={{ color: avgScore >= 75 ? '#10b981' : avgScore >= 60 ? '#f59e0b' : '#38bdf8' }}>
                  {avgScore}%
                </strong>
                {record.max_percentage > 0 && (
                  <span style={{ marginLeft: 6, color: '#ec4899', fontSize: 10 }}>
                    (Top: {record.max_percentage}%)
                  </span>
                )}
              </div>
            )}
          </div>
        );
      }
    },
    { 
      title: 'Head of Institution', 
      dataIndex: 'institution_head', 
      key: 'institution_head',
      width: 170,
      render: text => text || <span style={{ color: 'rgba(255,255,255,0.3)' }}>N/A</span>
    },
    {
      title: 'Contact & Email',
      key: 'contact_info',
      width: 180,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {record.institution_contact && <span>📞 {record.institution_contact}</span>}
          {record.institution_email && <span style={{ fontSize: '0.85em', color: 'var(--text-secondary, #4b5563)' }}>✉️ {record.institution_email}</span>}
          {!record.institution_contact && !record.institution_email && <span style={{ color: '#9ca3af' }}>N/A</span>}
        </div>
      )
    },
    { 
      title: 'Address & Map', 
      key: 'address_map',
      width: 180,
      render: (_, record) => {
        const address = record.institution_address;
        const mapUrl = record.institution_map;
        const isUrl = mapUrl && (mapUrl.startsWith('http://') || mapUrl.startsWith('https://'));

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {address ? (
              <div style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{address}</div>
            ) : (
              <span style={{ color: '#9ca3af' }}>N/A</span>
            )}
            {mapUrl && (
              <div style={{ marginTop: 2 }}>
                <Button 
                  type="link" 
                  size="small"
                  icon={<EnvironmentOutlined />} 
                  style={{ padding: 0, height: 'auto', display: 'inline-flex', alignItems: 'center' }}
                  onClick={() => {
                    if (isUrl) {
                      window.open(mapUrl, '_blank');
                    } else {
                      Modal.info({
                        title: 'Institution Map Code/Details',
                        content: <pre style={{ maxHeight: 300, overflow: 'auto' }}>{mapUrl}</pre>,
                        okText: 'Close'
                      });
                    }
                  }}
                >
                  View Map
                </Button>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      render: (_, record) => (
        <Space>
          <Tooltip title="Academic Year Candidate Comparison">
            <Button 
              size="small" 
              icon={<BarChartOutlined />} 
              style={{ color: '#6366f1', borderColor: '#6366f1' }}
              onClick={() => handleOpenComparison(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Institution">
            <Button 
              size="small" 
              icon={<EditOutlined />} 
              onClick={() => { 
                setEditingItem(record); 
                form.setFieldsValue(record); 
                setIsModalOpen(true); 
              }} 
            />
          </Tooltip>
          <Popconfirm title="Delete this institution?" onConfirm={() => handleDelete(record.institution_id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: 'var(--text-color)', margin: 0 }}>Institution Management</h2>
        </div>
        <Space>
          <Button 
            type="default" 
            icon={<LineChartOutlined />} 
            style={{ borderColor: '#10b981', color: '#10b981' }}
            onClick={handleOpenOverview}
          >
            Multi-Year Overview (All Insts)
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); form.resetFields(); setIsModalOpen(true); }}>
            Add Institution
          </Button>
        </Space>
      </div>

      {/* Filter Options Panel */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input 
          placeholder="Search by name or address..." 
          onChange={(e) => setSearchText(e.target.value)} 
          value={searchText} 
          style={{ width: 300, borderRadius: 6 }} 
          allowClear 
        />
        <Select showSearch optionFilterProp="children" 
          placeholder="All Mediums" 
          onChange={(value) => setSelectedMedium(value)} 
          value={selectedMedium} 
          style={{ width: 180 }} 
          allowClear
        >
          <Select.Option value="KANNADA">KANNADA</Select.Option>
          <Select.Option value="ENGLISH">ENGLISH</Select.Option>
          <Select.Option value="KANNADA/ENGLISH">KANNADA/ENGLISH</Select.Option>
        </Select>
        {(searchText || selectedMedium) && (
          <Button type="link" onClick={handleClearFilters} style={{ padding: 0 }}>
            Clear Filters
          </Button>
        )}
      </div>

      <Card style={{ border: '1px solid var(--border-color)', borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
        <Table 
          dataSource={filteredData} 
          columns={columns} 
          rowKey="institution_id" 
          loading={loading} 
          className="custom-dark-table"
          scroll={{ x: 'max-content' }}
          pagination={{ 
            pageSize: 20, 
            defaultPageSize: 20, 
            showSizeChanger: true, 
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} institutions`
          }}
        />
      </Card>

      {/* Edit / Add Institution Modal */}
      <Modal 
        title={editingItem ? 'Edit Institution' : 'Add Institution'} 
        open={isModalOpen} 
        onCancel={() => { setIsModalOpen(false); form.resetFields(); setEditingItem(null); }} 
        footer={null} 
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="institution_name" label="Institution Name" rules={[{ required: true, message: 'Please enter institution name' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="institution_head" label="Head of Institution">
            <Input />
          </Form.Item>
          <Form.Item 
            name="institution_email" 
            label="Email" 
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="institution_contact" label="Contact Number">
            <Input />
          </Form.Item>
          <Form.Item 
            name="institution_medium" 
            label="Medium of Instruction" 
            rules={[{ required: true, message: 'Please select medium' }]}
            initialValue="ENGLISH"
          >
            <Select showSearch optionFilterProp="children">
              <Select.Option value="KANNADA">KANNADA</Select.Option>
              <Select.Option value="ENGLISH">ENGLISH</Select.Option>
              <Select.Option value="KANNADA/ENGLISH">KANNADA/ENGLISH</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="institution_address" label="Address">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="institution_map" label="Map URL / Embed Code">
            <Input.TextArea rows={2} placeholder="https://maps.google.com/... or iframe code" />
          </Form.Item>
          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button onClick={() => { setIsModalOpen(false); form.resetFields(); setEditingItem(null); }}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Single Institution Academic Year Comparison Modal */}
      <InstitutionAcademicYearComparisonModal
        open={isComparisonModalOpen}
        onClose={() => {
          setIsComparisonModalOpen(false);
          setSelectedInstitution(null);
          setComparisonData(null);
        }}
        data={comparisonData}
        loading={comparisonLoading}
        onRefresh={handleRefreshComparison}
        onMediumChange={handleComparisonMediumChange}
        selectedMedium={comparisonSelectedMedium}
      />

      {/* Cross-Institution Multi-Year Overview Modal */}
      <CrossInstitutionComparisonModal
        open={isOverviewModalOpen}
        onClose={() => setIsOverviewModalOpen(false)}
        data={overviewData}
        loading={overviewLoading}
        onRefresh={() => handleOpenOverview()}
      />
    </div>
  );
};

export default InstitutionManagementPage;
